/**
 * IntroScreen.js
 * 
 * A beautiful single-page landing screen that appears before the login screen.
 * Modern, scrollable layout similar to Notion/Linear style with scroll-driven animations.
 * 
 * Uses the existing UI theme (Oura-inspired warm wellness design)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronRight, Camera, TrendingUp, Brain, Shield, Zap, Award, BarChart3, Check } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const TOTAL_FRAMES = 192;
const PRELOAD_COUNT = 24;
const FRAME_STEP = 2;

// ─── Feature Cards Data ───────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: Camera,
    iconBg: 'linear-gradient(135deg, #0D9488 0%, #115E59 100%)',
    title: 'Photo Tracking and Timelapse',
    description: 'Capture progress photos and see your transformation with side-by-side comparisons and genarate timelapse videos of your progress over time.',
  },
  {
    icon: TrendingUp,
    iconBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    title: 'Build Consistency',
    description: 'Daily routines, streaks, and reminders keep you on track with your treatment plan.',
  },
  {
    icon: Brain,
    iconBg: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    title: 'Insights',
    description: 'Get personalized analysis and chat with Dr.Strand for science-backed guidance ',
  },
];

// ─── Benefits Data ────────────────────────────────────────────────────────────
const BENEFITS = [
  { icon: Shield, text: 'Private & secure — your data stays yours' },
  { icon: Zap, text: 'Takes only 30 seconds per day' },
  { icon: Award, text: 'Evidence-based protocols' },
  { icon: BarChart3, text: 'Track real progress over time' },
];

// ─── Storytelling Captions ────────────────────────────────────────────────────
const STORY_CAPTIONS = [
  { range: [0, 0.5], title: "Your hair journey,\nsimplified.", subtitle: "Take control of your hair health." },
{
  range: [0.5, 1.0],
  title: "Your hair journey starts \nwith small steps.",
  subtitle: "Stay consistent, track your progress, and watch real change happen over time."
}
];

// ─── Preload All Static Frame Paths ───────────────────────────────────────────
const FRAME_URLS = [];
for (let i = 0; i < TOTAL_FRAMES; i++) {
  try {
    // Webpack will process these and create static string URL lists
    FRAME_URLS.push(require(`../assets/frames/frame_${String(i).padStart(3, '0')}.webp`));
  } catch (e) {
    FRAME_URLS.push(''); // fallback
  }
}

// ─── Navigation Links ─────────────────────────────────────────────────────────
const NAV_LINKS = [
  { label: 'Home', target: 'hero' },
  { label: 'Features', target: 'features' },
  { label: 'Pricing', target: 'pricing' },
  { label: 'How it Works', target: 'benefits' },
];

// ─── Custom Hook: Intersection Observer for animations ───────────────────────
function useIntersectionObserver(options = {}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0.1, ...options }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options]);

  return [ref, isVisible];
}

// ─── Frame Preloader ──────────────────────────────────────────────────────────
const preloadedImages = [];
let fullPreloadStarted = false;

function preloadFrames(count) {
  for (let i = 0; i < count; i++) {
    if (FRAME_URLS[i]) {
      const img = new Image();
      img.src = FRAME_URLS[i];
      // explicitly decode the image to prevent main thread jank on first draw to canvas
      if (img.decode) {
        img.decode().catch(() => {});
      }
      preloadedImages.push(img);
    }
  }
}

function preloadRemainingFramesInBackground() {
  if (fullPreloadStarted) return;
  fullPreloadStarted = true;

  const startIndex = Math.max(PRELOAD_COUNT, 0);
  const run = () => {
    for (let i = startIndex; i < TOTAL_FRAMES; i += 1) {
      if (!FRAME_URLS[i]) continue;
      if (preloadedImages[i]) continue;
      const img = new Image();
      img.src = FRAME_URLS[i];
      if (img.decode) img.decode().catch(() => {});
      preloadedImages[i] = img;
    }
  };

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 250);
  }
}

// ─── Main IntroScreen Component ────────────────────────────────────────────────
export default function IntroScreen({ onComplete }) {
  // ─── State & Refs ───────────────────────────────────────────────────────────
  const [ctaVisible, setCtaVisible] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const containerRef = useRef(null);
  const animationContainerRef = useRef(null);
  const ctaRef = useRef(null);
  
  // Refs for direct DOM manipulation to avoid React render lag on scroll
  const imgRef = useRef(null);
  const captionRefs = useRef([]);
  const scrollIndicatorRef = useRef(null);

  // ─── Preload frames on mount ────────────────────────────────────────────────
  useEffect(() => {
    preloadFrames(PRELOAD_COUNT);
    preloadRemainingFramesInBackground();
  }, []);

  // ─── Resize observer for canvas ─────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      if (imgRef.current) {
        const parent = imgRef.current.parentElement;
        imgRef.current.width = parent.clientWidth;
        imgRef.current.height = parent.clientHeight;
        
        // Force a scroll event to redraw the current frame
        if (containerRef.current) {
          containerRef.current.dispatchEvent(new Event('scroll'));
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial sizing
    
    // Draw initial frame if ready
    const triggerInitialDraw = () => {
      if (preloadedImages[0] && preloadedImages[0].complete && containerRef.current) {
        containerRef.current.dispatchEvent(new Event('scroll'));
      } else {
        setTimeout(triggerInitialDraw, 100);
      }
    };
    triggerInitialDraw();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ─── Scroll handler ─────────────────────────────────────────
  const scrollMetrics = useRef({ range: 1 });
  const lastDrawnFrame = useRef(-1); // To prevent redundant canvas draws causing lag

  useEffect(() => {
    const updateMetrics = () => {
      const hero = document.getElementById('hero');
      const container = containerRef.current;
      if (hero && container) {
        // Cache layout metrics to prevent layout thrashing on scroll
        scrollMetrics.current.range = hero.offsetHeight - container.clientHeight;
      }
    };
    
    updateMetrics();
    window.addEventListener('resize', updateMetrics);
    return () => window.removeEventListener('resize', updateMetrics);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = container.scrollTop;
          const scrollRange = scrollMetrics.current.range;
            
          if (scrollRange > 0) {
            const progress = Math.max(0, Math.min(1, scrolled / scrollRange));
            
            // 1. Update image frame directly on Canvas
            const reducedFrameCount = Math.max(1, Math.ceil(TOTAL_FRAMES / FRAME_STEP));
            const reducedIndex = Math.min(Math.floor(progress * reducedFrameCount), reducedFrameCount - 1);
            const frameIndex = Math.min(reducedIndex * FRAME_STEP, TOTAL_FRAMES - 1);
            
            // Optimization: Only redraw if the frame index actually changed
            if (frameIndex !== lastDrawnFrame.current && imgRef.current && preloadedImages[frameIndex] && preloadedImages[frameIndex].complete) {
              const canvas = imgRef.current;
              const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
              const img = preloadedImages[frameIndex];
              
              const canvasRatio = canvas.width / canvas.height;
              const imgRatio = img.width / img.height;
              let drawWidth, drawHeight, offsetX, offsetY;

              if (canvasRatio > imgRatio) {
                drawWidth = canvas.width;
                drawHeight = drawWidth / imgRatio;
                offsetX = 0;
                offsetY = (canvas.height - drawHeight) / 2;
              } else {
                drawHeight = canvas.height;
                drawWidth = drawHeight * imgRatio;
                offsetX = (canvas.width - drawWidth) / 2;
                offsetY = 0;
              }

              ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
              lastDrawnFrame.current = frameIndex;
            }

            // 2. Update storytelling text opacity and translation based on progress
            STORY_CAPTIONS.forEach((caption, index) => {
              const el = captionRefs.current[index];
              if (!el) return;

              const fadeEdge = 0.05; // 5% of scroll range for fading in/out
              let opacity = 0;
              let translateY = 20;

              if (progress >= caption.range[0] && progress <= caption.range[1]) {
                opacity = 1;
                translateY = 0;

                // Fade in at the start of its range
                if (progress < caption.range[0] + fadeEdge && caption.range[0] > 0) {
                  opacity = (progress - caption.range[0]) / fadeEdge;
                  translateY = 20 * (1 - opacity);
                } 
                // Fade out at the end of its range
                else if (progress > caption.range[1] - fadeEdge) {
                  opacity = (caption.range[1] - progress) / fadeEdge;
                  translateY = -20 * (1 - opacity); // Move up slightly
                }
              }

              el.style.opacity = opacity;
              el.style.transform = `translate3d(0, ${translateY}px, 0)`; // force GPU acceleration
              el.style.pointerEvents = opacity > 0 ? 'auto' : 'none';
            });

            // 3. Fade out the scroll indicator based on initial progress
            if (scrollIndicatorRef.current) {
              const indicatorOpacity = Math.max(0, 1 - (progress / 0.05));
              scrollIndicatorRef.current.style.opacity = indicatorOpacity;
              // Add simple bounce animation using translateY if opacity is near full, else settle
              if (indicatorOpacity > 0.1) {
                 const bounce = Math.sin(Date.now() / 200) * 5;
                 scrollIndicatorRef.current.style.transform = `translateY(${bounce}px)`;
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // ─── CTA visibility observer ────────────────────────────────────────────────
  useEffect(() => {
    const cta = ctaRef.current;
    if (!cta) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCtaVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(cta);
    return () => observer.disconnect();
  }, []);

  // ─── Smooth scroll to section ───────────────────────────────────────────────
  const scrollToSection = useCallback((sectionId) => {
    const section = document.getElementById(sectionId);
    if (section && containerRef.current) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // ─── Styles ─────────────────────────────────────────────────────────────────────
  
  // Navbar styles
  const navbarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '64px',
    background: 'rgba(247, 245, 240, 0.9)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    zIndex: 100,
  };

  const navLogoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontWeight: '700',
    fontSize: '1.1rem',
    color: '#2A2824',
    cursor: 'pointer',
  };

  const navLogoIconStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: '#3B3833',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#F7F5F0',
    fontSize: '1rem',
    fontWeight: '700',
  };

  const navLinksStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const navLinkStyle = {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#8A8171',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const navCtaStyle = {
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    background: '#3B3833',
    color: '#F7F5F0',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginLeft: '8px',
  };

  // Animation container styles
  const animationContainerStyle = {
    position: 'sticky',
    top: 0,
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    zIndex: 10,
  };

  const frameImageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
  };

  const containerStyle = {
    height: '100vh',
    width: '100%',
    background: 'var(--background)',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const heroStyle = {
    position: 'relative',
    height: '310vh', // Decreased from 350vh to 175vh. THIS CONTROLS ANIMATION SPEED (shorter height = faster animation)
    width: '100%',
  };

  const logoStyle = {
    width: '72px',
    height: '72px',
    borderRadius: '20px',
    background: 'var(--foreground)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    boxShadow: '0 8px 32px rgba(42, 40, 36, 0.12)',
  };

  const headlineStyle = {
    fontSize: '2.25rem',
    fontWeight: '700',
    color: 'var(--foreground)',
    lineHeight: 1.15,
    letterSpacing: '-0.03em',
    marginBottom: '16px',
  };

  const subtitleStyle = {
    fontSize: '1.1rem',
    color: 'var(--muted-foreground)',
    lineHeight: 1.6,
    maxWidth: '440px',
    marginBottom: '32px',
  };

  const primaryBtnStyle = {
    padding: '16px 32px',
    borderRadius: '14px',
    border: 'none',
    background: 'var(--foreground)',
    color: 'var(--primary-foreground)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 16px rgba(42, 40, 36, 0.15)',
  };

  const sectionStyle = {
    padding: '48px 24px',
    maxWidth: '800px',
    margin: '0 auto',
  };

  const sectionTitleStyle = {
    fontSize: '0.75rem',
    fontWeight: '700',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--primary)',
    marginBottom: '12px',
    textAlign: 'center',
  };

  const sectionHeadingStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'var(--foreground)',
    marginBottom: '32px',
    textAlign: 'center',
    letterSpacing: '-0.01em',
  };

  const featureGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '20px',
  };

  const featureCardStyle = {
    background: 'var(--card)',
    borderRadius: '20px',
    border: '1px solid var(--border)',
    padding: '24px',
    boxShadow: 'var(--shadow-sm)',
  };

  const featureIconStyle = (bg) => ({
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  });

  const benefitsContainerStyle = {
    background: 'var(--foreground)',
    borderRadius: '20px',
    padding: '32px',
    boxShadow: '0 8px 32px rgba(42, 40, 36, 0.12)',
  };

  const benefitItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 0',
    borderBottom: '1px solid var(--border)',
  };

  const footerStyle = {
    padding: '48px 24px 60px',
    textAlign: 'center',
    maxWidth: '500px',
    margin: '0 auto',
  };

  return (
    <div style={containerStyle} ref={containerRef}>
      {/* Navigation Bar */}
      <nav style={navbarStyle}>
        <div 
          style={navLogoStyle} 
          onClick={() => scrollToSection('hero')}
        >
          <div style={navLogoIconStyle}>H</div>
          <span>HairRabbit</span>
        </div>
        <div style={navLinksStyle}>
          {NAV_LINKS.map((link) => (
            <button
              key={link.target}
              style={navLinkStyle}
              onClick={() => scrollToSection(link.target)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(138, 129, 113, 0.1)';
                e.currentTarget.style.color = '#2A2824';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#8A8171';
              }}
            >
              {link.label}
            </button>
          ))}
          <button
            style={navCtaStyle}
            onClick={onComplete}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2A2824';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#3B3833';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" style={heroStyle}>
        {/* Scroll-Driven Animation Container */}
        <div ref={animationContainerRef} style={animationContainerStyle}>
          
          {/* Base Layer: High Performance Canvas Sequence */}
          <canvas 
            ref={imgRef}
            style={frameImageStyle}
          />
          
          {/* Overlay Layer: Storytelling Captions */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            pointerEvents: 'none',
          }}>
            {STORY_CAPTIONS.map((caption, i) => (
              <div 
                key={i} 
                ref={el => captionRefs.current[i] = el}
                style={{
                  position: 'absolute',
                  top: '100px', // Below the sticky navbar
                  left: '0',
                  width: '100%',
                  padding: '24px 48px', // Padding on left and right so it doesn't touch the edges completely
                  transform: i === 0 ? 'translate3d(0, 0px, 0)' : 'translate3d(0, 20px, 0)',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  opacity: i === 0 ? 1 : 0,
                  willChange: 'opacity, transform',
                  pointerEvents: 'none', // prevent capturing clicks
                }}
              >
                {i === 0 && (
                  <div style={{ ...logoStyle, marginBottom: '24px' }}>
                    <span style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-foreground)' }}>H</span>
                  </div>
                )}
                
                <h1 style={{
                  fontSize: '3.5rem',
                  fontWeight: '700',
                  color: 'var(--foreground)',
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  marginBottom: '16px',
                  whiteSpace: 'pre-line',
                  textShadow: '0 4px 20px rgba(247, 245, 240, 0.4)' // slight glow to ensure readability
                }}>
                  {caption.title}
                </h1>
                
                <p style={{
                  fontSize: '1.25rem',
                  color: 'var(--foreground)',
                  fontWeight: '500',
                  lineHeight: 1.6,
                  maxWidth: '440px',
                  marginBottom: i === 0 ? '32px' : '0',
                }}>
                  {caption.subtitle}
                </p>

                {i === 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'flex-start', pointerEvents: 'auto' }}>
                    <button
                      style={{ ...primaryBtnStyle, pointerEvents: 'auto' }}
                      onClick={onComplete}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 24px rgba(42, 40, 36, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(42, 40, 36, 0.15)';
                      }}
                    >
                      Get Started <ChevronRight size={18} />
                    </button>

                    <a
                      href="https://play.google.com/store/apps/details?id=com.hairrabbit.app"
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <img
                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        alt="Also available on Google Play Store"
                        style={{ width: '190px', height: 'auto', display: 'block' }}
                      />
                    </a>
                  </div>
                )}
              </div>
            ))}

            {/* Scroll Indication */}
            {/* Scroll Indication */}
<div 
  ref={scrollIndicatorRef}
  style={{
    position: 'absolute',
    bottom: '48px',
    right: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px', // Slightly more gap for the larger icon
    opacity: 1,
    transition: 'opacity 0.2s',
    pointerEvents: 'none'
  }}
>
  <div style={{
    width: '32px',          // Increased from 24px
    height: '64px',         // Increased from 50px
    borderRadius: '16px',   // Adjusted for larger width
    border: '2.5px solid rgba(42, 40, 36, 0.4)', // Slightly thicker border
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '8px'       // Deeper starting point
  }}>
    <div style={{
      width: '5px',         // Slightly wider wheel
      height: '10px',       // Slightly longer wheel
      backgroundColor: 'rgba(42, 40, 36, 0.6)',
      borderRadius: '3px',
      animation: 'scrollWheel 1.8s infinite ease-in-out' // Smoother timing
    }} />
  </div>
  <span style={{ 
    fontSize: '0.95rem',    // Slightly larger text
    fontWeight: '700',      // Bolder weight
    color: 'rgba(42, 40, 36, 0.6)',
    letterSpacing: '0.02em',
    textTransform: 'uppercase' // More professional/minimalist look
  }}>
    Scroll to explore
  </span>
  <style>
    {`
      @keyframes scrollWheel {
        0% { transform: translateY(0); opacity: 0; }
        20% { opacity: 1; }
        80% { opacity: 1; }
        100% { transform: translateY(20px); opacity: 0; } 
      }
    `}
  </style>
</div>

          </div>
        </div>
      </section>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--border)', margin: '0 24px' }} />

      {/* Features Section */}
      <section id="features" style={sectionStyle}>
        <p style={sectionTitleStyle}>Features</p>
        <h2 style={sectionHeadingStyle}>Everything you need to track progress</h2>
        <div style={featureGridStyle}>
          {FEATURES.map((feature, i) => (
            <FeatureCard key={i} feature={feature} index={i} />
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{ ...sectionStyle, paddingTop: '48px', paddingBottom: '48px', maxWidth: '900px' }}>
        <p style={sectionTitleStyle}>Pricing</p>
        <h2 style={sectionHeadingStyle}>Choose your plan</h2>
        
        {/* Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '48px' }}>
          <div style={{ background: 'var(--card)', borderRadius: '100px', border: '1px solid var(--border)', padding: '6px', display: 'flex', boxShadow: 'var(--shadow-sm)' }}>
            <button 
              onClick={() => setBillingCycle('monthly')}
              style={{ 
                padding: '12px 28px', 
                borderRadius: '100px', 
                border: 'none', 
                background: billingCycle === 'monthly' ? 'var(--foreground)' : 'transparent',
                color: billingCycle === 'monthly' ? 'var(--background)' : 'var(--muted-foreground)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '0.95rem'
              }}>
              Monthly
            </button>
            <button 
              onClick={() => setBillingCycle('yearly')}
              style={{ 
                padding: '12px 28px', 
                borderRadius: '100px', 
                border: 'none', 
                background: billingCycle === 'yearly' ? 'var(--foreground)' : 'transparent',
                color: billingCycle === 'yearly' ? 'var(--background)' : 'var(--muted-foreground)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '0.95rem'
              }}>
              Yearly <span style={{ color: billingCycle === 'yearly' ? '#D1D5DB' : 'var(--primary)', fontWeight: 'bold' }}>(Save 20%)</span>
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
          {/* Free Tier */}
          <div style={{
            background: 'var(--card)',
            borderRadius: '24px',
            border: '1px solid var(--border)',
            padding: '40px 32px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px' }}>Basic</h3>
            <p style={{ color: 'var(--muted-foreground)', marginBottom: '32px', minHeight: '48px' }}>Essential tracking for your hair health journey.</p>
            <div style={{ marginBottom: '32px' }}>
              <span style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--foreground)' }}>$0</span>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 40px 0', flex: 1 }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--foreground)', fontWeight: '500' }}>
                <Check size={20} color="var(--primary)" /> Photo Tracking
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--foreground)', fontWeight: '500' }}>
                <Check size={20} color="var(--primary)" /> Daily Routines
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--foreground)', fontWeight: '500' }}>
                <Check size={20} color="var(--primary)" /> Streak Tracking
              </li>
            </ul>
            <button 
              onClick={onComplete}
              style={{ ...primaryBtnStyle, width: '100%', background: 'transparent', border: '2px solid var(--border)', color: 'var(--foreground)', boxShadow: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--foreground)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              Start Free
            </button>
          </div>

          {/* Premium Tier */}
          <div style={{
            background: 'var(--card)',
            borderRadius: '24px',
            border: '2px solid var(--foreground)',
            padding: '40px 32px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 40px rgba(42, 40, 36, 0.1)',
            position: 'relative',
          }}>
            <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: 'var(--foreground)', color: 'var(--background)', padding: '6px 16px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '0.05em' }}>
              RECOMMENDED
            </div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px' }}>Premium</h3>
            <p style={{ color: 'var(--muted-foreground)', marginBottom: '32px', minHeight: '48px' }}>AI-powered insights, coaching and long-term forecasting.</p>
            <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column' }}>
              <div>
                <span style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--foreground)' }}>
                  {billingCycle === 'monthly' ? '$10' : '$8'}
                </span>
                <span style={{ color: 'var(--muted-foreground)', fontSize: '1.2rem', fontWeight: '500' }}>/month</span>
              </div>
              <div style={{ height: '24px', marginTop: '4px' }}>
                {billingCycle === 'yearly' && <span style={{ fontSize: '0.9rem', color: 'var(--primary)', fontWeight: '600' }}>Billed $96 yearly</span>}
              </div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 40px 0', flex: 1 }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--foreground)', fontWeight: '500' }}>
                <Check size={20} color="var(--primary)" /> <span style={{ fontWeight: '700' }}>Everything in Basic</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--foreground)', fontWeight: '500' }}>
                <Check size={20} color="var(--primary)" /> AI Hair Coach
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', color: 'var(--foreground)', fontWeight: '500' }}>
                <Check size={20} color="var(--primary)" /> Advanced Analytics
              </li>
            </ul>
            <button 
              style={{ ...primaryBtnStyle, width: '100%' }} 
              onClick={onComplete}
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" style={{ ...sectionStyle, paddingTop: '24px' }}>
        <p style={sectionTitleStyle}>Why HairRabbit</p>
        <h2 style={sectionHeadingStyle}>Built for real results</h2>
        <div style={benefitsContainerStyle}>
          {BENEFITS.map((benefit, i) => (
            <BenefitRow key={i} benefit={benefit} index={i} isLast={i === BENEFITS.length - 1} />
          ))}
        </div>
      </section>

      {/* CTA Footer */}
      <section ref={ctaRef} style={footerStyle}>
        <p style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          color: 'var(--foreground)', 
          marginBottom: '12px' 
        }}>
          Ready to start tracking?
        </p>
        <p style={{ 
          fontSize: '0.9rem', 
          color: 'var(--muted-foreground)', 
          marginBottom: '24px', 
          lineHeight: 1.55 
        }}>
          Join thousands who are taking control of their hair health journey.
        </p>
        <button
          style={{ 
            ...primaryBtnStyle, 
            width: '100%', 
            maxWidth: '300px', 
            margin: '0 auto',
            transform: ctaVisible ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.3s ease',
          }}
          onClick={onComplete}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(42, 40, 36, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(42, 40, 36, 0.15)';
          }}
        >
          Get Started <ChevronRight size={18} />
        </button>
        <p style={{ 
          fontSize: '0.8rem', 
          color: 'var(--muted-foreground)', 
          marginTop: '16px',
          opacity: 0.7,
        }}>
          Free to use • No credit card required
        </p>
      </section>
    </div>
  );
}

// ─── Animated Feature Card Component ──────────────────────────────────────────
function FeatureCard({ feature, index }) {
  const [ref, isVisible] = useIntersectionObserver();
  
  const cardStyle = {
    background: 'var(--foreground)',
    borderRadius: '20px',
    padding: '32px 24px',
    boxShadow: '0 8px 32px rgba(42, 40, 36, 0.12)',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: `all 0.4s ease ${index * 100}ms`,
  };

  const iconStyle = {
    marginBottom: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div ref={ref} style={cardStyle}>
      <div style={iconStyle}>
        <feature.icon size={32} color="var(--background)" strokeWidth={1.5} />
      </div>
      <h3 style={{ 
        fontSize: '1.25rem', 
        fontWeight: '700', 
        color: 'var(--background)', 
        marginBottom: '8px',
        letterSpacing: '-0.01em'
      }}>
        {feature.title}
      </h3>
      <p style={{ 
        fontSize: '0.95rem', 
        color: 'rgba(247, 245, 240, 0.7)', 
        lineHeight: 1.55, 
        margin: 0 
      }}>
        {feature.description}
      </p>
    </div>
  );
}

// ─── Animated Benefit Row Component ───────────────────────────────────────────
function BenefitRow({ benefit, index, isLast }) {
  const [ref, isVisible] = useIntersectionObserver();

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 0',
    borderBottom: isLast ? 'none' : '1px solid rgba(247, 245, 240, 0.1)',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
    transition: `all 0.4s ease ${index * 80}ms`,
  };

  return (
    <div ref={ref} style={rowStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <benefit.icon size={24} color="var(--background)" strokeWidth={1.5} />
      </div>
      <span style={{ 
        fontSize: '1.05rem', 
        color: 'var(--background)', 
        fontWeight: '500' 
      }}>
        {benefit.text}
      </span>
    </div>
  );
}
